import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import ParabolaShape from "../../../../components/visuals/ParabolaShape";

function randInt(min, max, exclude = []) {
  let n;
  do {
    n = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (exclude.includes(n));
  return n;
}

function choice(items) {
  return items[Math.floor(Math.random() * items.length)];
}

function cubicLatex(a, b, c, d) {
  const parts = [];
  parts.push(a === 1 ? "x^3" : a === -1 ? "-x^3" : `${a}x^3`);
  if (b !== 0) {
    const part = Math.abs(b) === 1 ? "x^2" : `${Math.abs(b)}x^2`;
    parts.push(b > 0 ? `+ ${part}` : `- ${part}`);
  }
  if (c !== 0) {
    const part = Math.abs(c) === 1 ? "x" : `${Math.abs(c)}x`;
    parts.push(c > 0 ? `+ ${part}` : `- ${part}`);
  }
  if (d !== 0) {
    parts.push(d > 0 ? `+ ${d}` : `- ${Math.abs(d)}`);
  }
  return parts.join(" ");
}

function quadraticLatex(a, b, c) {
  const parts = [];
  parts.push(a === 1 ? "x^2" : a === -1 ? "-x^2" : `${a}x^2`);
  if (b !== 0) {
    const part = Math.abs(b) === 1 ? "x" : `${Math.abs(b)}x`;
    parts.push(b > 0 ? `+ ${part}` : `- ${part}`);
  }
  if (c !== 0) {
    parts.push(c > 0 ? `+ ${c}` : `- ${Math.abs(c)}`);
  }
  return parts.join(" ");
}

function cubicValue(a, b, c, d, x) {
  return a * x * x * x + b * x * x + c * x + d;
}

function buildCubicTask() {
  for (let attempt = 0; attempt < 800; attempt += 1) {
    const x1 = randInt(-4, 2);
    const x2 = randInt(x1 + 1, 4);
    const s = choice([-1, 1]);
    const d = randInt(-4, 4);

    const ca = 2 * s;
    const cb = -3 * s * (x1 + x2);
    const cc = 6 * s * x1 * x2;
    const cd = d;

    const da = 6 * s;
    const db = -6 * s * (x1 + x2);
    const dc = 6 * s * x1 * x2;

    const y1 = cubicValue(ca, cb, cc, cd, x1);
    const y2 = cubicValue(ca, cb, cc, cd, x2);

    if (Math.abs(y1) > 6 || Math.abs(y2) > 6) continue;

    const leftSign = s > 0 ? "+" : "-";
    const middleSign = s > 0 ? "-" : "+";
    const rightSign = s > 0 ? "+" : "-";

    const maxX = s > 0 ? x1 : x2;
    const minX = s > 0 ? x2 : x1;
    const maxY = s > 0 ? y1 : y2;
    const minY = s > 0 ? y2 : y1;

    const targetType = choice(["maksimum lokalne", "minimum lokalne"]);
    const targetX = targetType === "maksimum lokalne" ? maxX : minX;
    const targetY = targetType === "maksimum lokalne" ? maxY : minY;

    return {
      cubicA: ca,
      cubicB: cb,
      cubicC: cc,
      cubicD: cd,
      derivativeA: da,
      derivativeB: db,
      derivativeC: dc,
      x1,
      x2,
      y1,
      y2,
      leftSign,
      middleSign,
      rightSign,
      targetType,
      targetX,
      targetY,
      functionLatex: cubicLatex(ca, cb, cc, cd),
      derivativeLatex: quadraticLatex(da, db, dc),
    };
  }

  const x1 = -1;
  const x2 = 2;
  const s = 1;
  const d = 0;
  return {
    cubicA: 2,
    cubicB: -3,
    cubicC: -12,
    cubicD: d,
    derivativeA: 6,
    derivativeB: -6,
    derivativeC: -12,
    x1,
    x2,
    y1: cubicValue(2, -3, -12, d, x1),
    y2: cubicValue(2, -3, -12, d, x2),
    leftSign: "+",
    middleSign: "-",
    rightSign: "+",
    targetType: "maksimum lokalne",
    targetX: x1,
    targetY: cubicValue(2, -3, -12, d, x1),
    functionLatex: cubicLatex(2, -3, -12, d),
    derivativeLatex: quadraticLatex(6, -6, -12),
  };
}

export function generateTask() {
  const data = buildCubicTask();
  return {
    type: "CubicLocalExtremumTask",
    prompt: `Wyznacz ${data.targetType} funkcji f(x) = ${data.functionLatex} metoda pochodnej. Podaj wspolrzedne punktu tego ekstremum.`,
    answer: `x_E=${data.targetX}, y_E=${data.targetY}`,
    data,
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>Wyznacz <strong>{data.targetType}</strong> funkcji:</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <p>Podaj wspolrzedne punktu tego ekstremum.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer && typeof userAnswer === "object" ? userAnswer : {};
  return (
    <div className="space-y-3">
      <div>
        <div style={{ marginBottom: "0.35rem" }}><InlineMath math="x_E =" /></div>
        <FractionInput
          value={value.answerX || ""}
          onChange={(next) => onChange({ ...value, answerX: next })}
          disabled={disabled}
          label="x_E"
        />
      </div>
      <div>
        <div style={{ marginBottom: "0.35rem" }}><InlineMath math="y_E =" /></div>
        <FractionInput
          value={value.answerY || ""}
          onChange={(next) => onChange({ ...value, answerY: next })}
          disabled={disabled}
          label="y_E"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const xValue = userAnswer.answerX?.parsed;
  const yValue = userAnswer.answerY?.parsed;
  return typeof xValue === "number"
    && typeof yValue === "number"
    && Math.abs(xValue - data.targetX) < 0.0001
    && Math.abs(yValue - data.targetY) < 0.0001;
}

export function renderUserAnswer(_taskOrAnswer, maybeUserAnswer) {
  const userAnswer = maybeUserAnswer !== undefined ? maybeUserAnswer : _taskOrAnswer;
  if (!userAnswer || typeof userAnswer !== "object") return <i>(brak odpowiedzi)</i>;
  const rawX = userAnswer.answerX?.raw;
  const rawY = userAnswer.answerY?.raw;
  if (!rawX && !rawY) return <i>(brak odpowiedzi)</i>;
  return (
    <div className="space-y-2">
      {rawX ? <div><InlineMath math={formatLatexPreview(rawX, "x_E")} /></div> : <div><InlineMath math={"x_E = ?"} /></div>}
      {rawY ? <div><InlineMath math={formatLatexPreview(rawY, "y_E")} /></div> : <div><InlineMath math={"y_E = ?"} /></div>}
      {rawX && rawY ? <div><InlineMath math={`E = (${formatLatexPreview(rawX)}, ${formatLatexPreview(rawY)})`} /></div> : null}
    </div>
  );
}

export function renderAnswer({ data }) {
  return (
    <div className="space-y-1">
      <div><InlineMath math={`x_E = ${data.targetX}`} /></div>
      <div><InlineMath math={`y_E = ${data.targetY}`} /></div>
      <div><InlineMath math={`E = (${data.targetX}, ${data.targetY})`} /></div>
    </div>
  );
}

export function renderExplanation({ data }) {
  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p><strong>Krok 1:</strong> Liczymy pochodna funkcji.</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <BlockMath math={`f'(x) = ${data.derivativeLatex}`} />

      <p><strong>Krok 2:</strong> Wyznaczamy miejsca zerowe pochodnej.</p>
      <BlockMath math={`${data.derivativeLatex} = 0`} />
      <BlockMath math={`x_1 = ${data.x1},\\quad x_2 = ${data.x2}`} />

      <p><strong>Krok 3:</strong> Sprawdzamy zmiane znaku pochodnej na szkicu wykresu <InlineMath math={"f'(x)"} />.</p>
      <div style={{ maxWidth: 560, margin: "20px auto" }}>
        <ParabolaShape
          aSign={data.derivativeA}
          width={560}
          height={200}
          rootMode="two"
          rootLeft={-2}
          rootRight={2}
          labelLeft={String(data.x1)}
          labelRight={String(data.x2)}
          showXAxis
          showZeroTicks
          xRange={5}
          yWindow={5}
        />
      </div>
      <p>
        Znak pochodnej uklada sie tak:
        {" "}
        <strong>{data.leftSign}</strong>, potem <strong>{data.middleSign}</strong>, a nastepnie <strong>{data.rightSign}</strong>.
        {" "}
        Oznacza to, ze funkcja kolejno
        {" "}
        <strong>{data.leftSign === "+" ? "rosnie" : "maleje"}</strong>,
        potem
        {" "}
        <strong>{data.middleSign === "+" ? "rosnie" : "maleje"}</strong>,
        a nastepnie znowu
        {" "}
        <strong>{data.rightSign === "+" ? "rosnie" : "maleje"}</strong>.
        {" "}
        Dlatego funkcja ma maksimum lokalne w <InlineMath math={`x = ${data.targetType === "maksimum lokalne" ? data.targetX : (data.targetX === data.x1 ? data.x2 : data.x1)}`} />
        {" "}
        i minimum lokalne w <InlineMath math={`x = ${data.targetType === "minimum lokalne" ? data.targetX : (data.targetX === data.x1 ? data.x2 : data.x1)}`} />.
      </p>

      <p><strong>Krok 4:</strong> Wybieramy z polecenia <strong>{data.targetType}</strong>.</p>
      <BlockMath math={`x_E = ${data.targetX}`} />

      <p><strong>Krok 5:</strong> Liczymy druga wspolrzedna punktu tego ekstremum.</p>
      <BlockMath math={`f(${data.targetX}) = ${data.targetY}`} />
      <BlockMath math={`E = (${data.targetX}, ${data.targetY})`} />
    </div>
  );
}
